import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  ApiResponse,
  QuizAnswer,
  QuizResult,
  TrainingContent,
  TrainingModule,
  TrainingModuleDetail,
  TrainingModuleListItem,
  TrainingQuiz,
  TrainingQuizAdmin,
  TrainingSection,
  UploadTokenResponse,
} from './training.interface';

@Injectable({ providedIn: 'root' })
export class TrainingService {
  private apiUrl = `${environment.apiConfig.uri}/training`;

  constructor(private http: HttpClient) {}

  // ── Learner ──────────────────────────────────────────────────────────────

  getModules(): Observable<TrainingModuleListItem[]> {
    return this.http
      .get<ApiResponse<TrainingModuleListItem[]>>(this.apiUrl)
      .pipe(map(r => r.data));
  }

  getModule(id: string): Observable<TrainingModuleDetail> {
    return this.http
      .get<ApiResponse<TrainingModuleDetail>>(`${this.apiUrl}/${id}`)
      .pipe(map(r => r.data));
  }

  getContentUrl(moduleId: string, contentId: string): Observable<{ url: string; expiresAt: string }> {
    return this.http
      .get<ApiResponse<{ url: string; expiresAt: string }>>(`${this.apiUrl}/${moduleId}/content/${contentId}/url`)
      .pipe(map(r => r.data));
  }

  markContentComplete(moduleId: string): Observable<void> {
    return this.http.post<void>(`${this.apiUrl}/${moduleId}/progress/content-complete`, {});
  }

  getQuiz(moduleId: string): Observable<TrainingQuiz> {
    return this.http
      .get<ApiResponse<TrainingQuiz>>(`${this.apiUrl}/${moduleId}/quiz`)
      .pipe(map(r => r.data));
  }

  submitQuiz(moduleId: string, answers: QuizAnswer[]): Observable<QuizResult> {
    return this.http
      .post<ApiResponse<QuizResult>>(`${this.apiUrl}/${moduleId}/quiz/submit`, { answers })
      .pipe(map(r => r.data));
  }

  // ── Admin ─────────────────────────────────────────────────────────────────

  getAllModules(): Observable<TrainingModuleListItem[]> {
    return this.http
      .get<ApiResponse<TrainingModuleListItem[]>>(`${this.apiUrl}?all=1`)
      .pipe(map(r => r.data));
  }

  createModule(data: Partial<TrainingModule>): Observable<TrainingModule> {
    return this.http
      .post<ApiResponse<TrainingModule>>(this.apiUrl, data)
      .pipe(map(r => r.data));
  }

  updateModule(id: string, data: Partial<TrainingModule>): Observable<TrainingModule> {
    return this.http
      .put<ApiResponse<TrainingModule>>(`${this.apiUrl}/${id}`, data)
      .pipe(map(r => r.data));
  }

  deleteModule(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  // ── Sections ────────────────────────────────────────────────────────────────

  createSection(moduleId: string, data: { title: string; type: 'slideshow' | 'image' | 'video'; order?: number }): Observable<TrainingSection> {
    return this.http
      .post<ApiResponse<TrainingSection>>(`${this.apiUrl}/${moduleId}/sections`, data)
      .pipe(map(r => r.data));
  }

  updateSection(moduleId: string, sectionId: string, data: Partial<{ title: string; type: string; order: number }>): Observable<TrainingSection> {
    return this.http
      .put<ApiResponse<TrainingSection>>(`${this.apiUrl}/${moduleId}/sections/${sectionId}`, data)
      .pipe(map(r => r.data));
  }

  deleteSection(moduleId: string, sectionId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${moduleId}/sections/${sectionId}`);
  }

  // ── Content (section-scoped) ─────────────────────────────────────────────────

  getUploadToken(moduleId: string, sectionId: string, filename: string, mimeType: string): Observable<UploadTokenResponse> {
    return this.http
      .post<ApiResponse<UploadTokenResponse>>(`${this.apiUrl}/${moduleId}/sections/${sectionId}/content/upload-token`, { filename, mimeType })
      .pipe(map(r => r.data));
  }

  registerContent(moduleId: string, sectionId: string, data: {
    title: string;
    type: 'image' | 'pdf' | 'video';
    blobPath: string;
    mimeType?: string;
    caption?: string;
    order?: number;
    originalFileName?: string;
  }): Observable<TrainingContent> {
    return this.http
      .post<ApiResponse<TrainingContent>>(`${this.apiUrl}/${moduleId}/sections/${sectionId}/content`, data)
      .pipe(map(r => r.data));
  }

  updateContent(moduleId: string, sectionId: string, contentId: string, data: { title?: string; order?: number; caption?: string }): Observable<TrainingContent> {
    return this.http
      .put<ApiResponse<TrainingContent>>(`${this.apiUrl}/${moduleId}/sections/${sectionId}/content/${contentId}`, data)
      .pipe(map(r => r.data));
  }

  deleteContent(moduleId: string, sectionId: string, contentId: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${moduleId}/sections/${sectionId}/content/${contentId}`);
  }

  saveQuiz(moduleId: string, data: { passingScore: number; questions: any[] }): Observable<TrainingQuizAdmin> {
    return this.http
      .put<ApiResponse<TrainingQuizAdmin>>(`${this.apiUrl}/${moduleId}/quiz`, data)
      .pipe(map(r => r.data));
  }

  getQuizAdmin(moduleId: string): Observable<TrainingQuizAdmin> {
    return this.http
      .get<ApiResponse<TrainingQuizAdmin>>(`${this.apiUrl}/${moduleId}/quiz`)
      .pipe(map(r => r.data));
  }
}
